import {Breadcrumbs as MuiBreadcrumbs, Link, Typography} from '@mui/material';
import {CaretRight} from '@phosphor-icons/react';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router';
import {STATION_TABS} from '../dashboard/stations/stationDetail/stationTabs';
import {reportTitle} from '../../screens/stations/StationDetail/StationAnalysis/reportTitles';
import sensorConfig, {
  resolveVisualization,
  slugToSensorType,
} from '../../screens/stations/StationDetail/SensorCharts/sensorConfig';

type BreadcrumbItem = {
  label: string;
  path?: string;
};

type AppBreadcrumbsProps = {
  stationName?: string;
  customItems?: BreadcrumbItem[];
};

const AppBreadcrumbs: React.FC<AppBreadcrumbsProps> = ({
  stationName,
  customItems,
}) => {
  const {formatMessage} = useIntl();
  const {pathname, search} = useLocation();

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) return customItems;

    const items: BreadcrumbItem[] = [];

    // Parse pathname to build breadcrumbs
    if (pathname.startsWith('/app/stations')) {
      items.push({
        label: formatMessage(messages.stations.weatherStations),
        path: '/app/stations/',
      });

      if (pathname.includes('/manage')) {
        items.push({
          label: formatMessage(messages.stations.modifyFavoriteStations),
        });
      } else if (pathname.includes('/search')) {
        items.push({
          label: formatMessage(messages.stations.searchForLocation),
        });
      } else {
        // Check for station detail pages
        const stationMatch = pathname.match(/\/app\/stations\/(\d+)/);
        if (stationMatch) {
          if (stationName) {
            items.push({
              label: stationName,
              path: `/app/stations/${stationMatch[1]}`,
            });
          }

          // A tab's crumb is the tab's own label (STATION_TABS).
          const tab = STATION_TABS.find(
            ({segment}) => segment && pathname.includes(`/${segment}`),
          );
          // A report under the Analysis tab: the tab links back to the list,
          // and the report takes the title its card in the list shows.
          const report =
            tab?.value === 'analysis'
              ? pathname.match(/\/analysis\/([^/]+)\/?$/)
              : null;
          const title = report ? reportTitle(report[1], formatMessage) : null;
          if (/\/forecast-comparison\/?$/.test(pathname)) {
            // Under the Forecast tab it is opened from, which the crumb
            // leads back to. Checked before the tabs: the path contains
            // `/forecast`, and took the tab's crumb as its own last one.
            const forecastTab = STATION_TABS.find(
              ({value}) => value === 'forecast',
            )!;
            items.push({
              label: formatMessage(forecastTab.label),
              path: `/app/stations/${stationMatch[1]}/${forecastTab.segment}`,
            });
            items.push({
              label: formatMessage(
                messages.forecastModels.forecastModelComparison,
              ),
            });
          } else if (tab && title) {
            items.push({
              label: formatMessage(tab.label),
              path: `/app/stations/${stationMatch[1]}/analysis`,
            });
            items.push({label: title});
          } else if (tab) {
            items.push({label: formatMessage(tab.label)});
          } else if (/\/sun$/.test(pathname)) {
            // Anchored, unlike its neighbours: this branch sits above
            // `/sensors/`, and a bare `includes('/sun')` would swallow any
            // future sensor slug starting with "sun" — `sunshine`,
            // `sun-duration` — leaving it with the wrong crumb and no way to
            // notice. No current slug collides; the anchor is so that adding
            // one cannot.
            //
            // The page takes the card's own name, so the crumb is the label the
            // user just clicked rather than a second name for the same thing.
            items.push({
              label: formatMessage(messages.astral.sunriseAndSunset),
            });
          } else if (/\/moon$/.test(pathname)) {
            // Anchored for the same reason as `/sun` above: a bare
            // `includes('/moon')` would swallow a future `moonlight` sensor
            // slug. Both crumbs take the label from the card the user clicked.
            items.push({label: formatMessage(messages.astral.moon)});
          } else if (/\/warnings(\/[^/]+)?\/?$/.test(pathname)) {
            // The list and a warning on it (`/warnings/<id>`) are one page,
            // so one crumb. Anchored like `/sun`.
            items.push({label: formatMessage(messages.weatherWarnings.crumb)});
          } else if (pathname.includes('/sensors/')) {
            const sensorMatch = pathname.match(/\/sensors\/([^/]+)/);
            if (sensorMatch) {
              const sensorSlug = sensorMatch[1];
              const sType = slugToSensorType(sensorSlug);
              const config = sType ? sensorConfig[sType] : undefined;
              if (config) {
                const chartMatch = pathname.match(/\/sensors\/[^/]+\/(.+)/);
                items.push({
                  label: formatMessage(config.titleMessage),
                  path: chartMatch
                    ? `/app/stations/${stationMatch[1]}/sensors/${sensorSlug}`
                    : undefined,
                });
                if (chartMatch && sType) {
                  // The page's own resolution: a visualization id, or the
                  // older chart type and `?metric=` of a bookmark.
                  const metric =
                    new URLSearchParams(search).get('metric') ?? undefined;
                  const viz = resolveVisualization(
                    sType,
                    chartMatch[1],
                    metric,
                  );
                  if (viz) {
                    items.push({
                      label: formatMessage(viz.titleMessage),
                    });
                  }
                }
              }
            }
          }
        }
      }
    } else if (pathname.startsWith('/app/settings')) {
      items.push({
        label: formatMessage(messages.settings.settings),
        path: '/app/settings/',
      });

      if (pathname.includes('/services')) {
        items.push({
          label: formatMessage(messages.addons.weatherServices),
        });
      } else if (pathname.includes('/profile')) {
        items.push({
          label: formatMessage(messages.account.profile),
        });
      } else if (pathname.includes('/units')) {
        items.push({
          label: formatMessage(messages.settings.units),
        });
      }
    } else if (pathname.startsWith('/help')) {
      items.push({
        label: formatMessage(messages.common.help),
        path: '/help',
      });
    } else if (pathname.startsWith('/map')) {
      items.push({
        label: formatMessage(messages.weatherMap.weatherMap),
      });
    }

    return items;
  };

  const breadcrumbs = getBreadcrumbs();

  if (breadcrumbs.length === 0) return null;

  return (
    <MuiBreadcrumbs
      // MUI's default name is the English "breadcrumb" in every language.
      aria-label={formatMessage(messages.navigation.breadcrumbs)}
      separator={<CaretRight size={16} />}
      sx={{
        mb: 2,
        // Wraps: four crumbs (a station tab's sub-page) are wider than a
        // phone, and a row that cannot wrap widened the whole page.
        '& .MuiBreadcrumbs-ol': {
          flexWrap: 'wrap',
          rowGap: 0.25,
        },
        '& .MuiBreadcrumbs-li': {
          whiteSpace: 'nowrap',
        },
      }}>
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;

        if (isLast || !item.path) {
          return (
            <Typography
              key={index}
              sx={{
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '0.875rem',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={index}
            href={item.path}
            underline="hover"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',

              // Ink, not the brand fill: primary.main is 2.36:1 as text.
              '&:hover': {
                color: 'ink.main',
              },
            }}>
            {item.label}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};

export default AppBreadcrumbs;
